/**
 * CL-TRADES-SUPPLIER-COLLAB-001A — Global staff capability flags on users/{authUid}.
 *
 * Missing keys resolve to false. Admin bypass is evaluated separately in access models.
 */

export const STAFF_CAPABILITY_KEYS = Object.freeze({
  CAN_MANAGE_TRADE_DIRECTORY: "canManageTradeDirectory",
});

const BOOLEAN_STAFF_CAPABILITY_KEYS = Object.freeze([
  STAFF_CAPABILITY_KEYS.CAN_MANAGE_TRADE_DIRECTORY,
]);

function safeStr(value) {
  return String(value ?? "").trim();
}

function staffCapabilitiesObject(profile = null) {
  const raw = profile?.staffCapabilities;
  return raw && typeof raw === "object" && !Array.isArray(raw) ? raw : {};
}

export function readStaffCapability(profile, key) {
  if (!BOOLEAN_STAFF_CAPABILITY_KEYS.includes(key)) return false;
  return staffCapabilitiesObject(profile)[key] === true;
}

export function readCanManageTradeDirectory(profile) {
  return readStaffCapability(profile, STAFF_CAPABILITY_KEYS.CAN_MANAGE_TRADE_DIRECTORY);
}

export function mergeStaffCapabilityPatch(existingProfile = null, capabilityKey, enabled = false) {
  if (!BOOLEAN_STAFF_CAPABILITY_KEYS.includes(capabilityKey)) {
    return { ok: false, error: "Unsupported staff capability.", patch: null };
  }
  const existing = staffCapabilitiesObject(existingProfile);
  const nextCapabilities = { ...existing, [capabilityKey]: enabled === true };
  return {
    ok: true,
    patch: {
      staffCapabilities: nextCapabilities,
    },
  };
}

export function buildCanManageTradeDirectoryTogglePatch(existingProfile = null, enabled = false) {
  return mergeStaffCapabilityPatch(
    existingProfile,
    STAFF_CAPABILITY_KEYS.CAN_MANAGE_TRADE_DIRECTORY,
    enabled
  );
}

export function isStaffCapabilityGrantTarget(profile = null) {
  const role = safeStr(profile?.role).toLowerCase();
  const primary = safeStr(profile?.primaryUserType).toLowerCase();
  const authUid = safeStr(profile?.authUid || profile?.id);
  const docId = safeStr(profile?.id);
  const isAuthKeyedStaffProfile = Boolean(authUid && docId && authUid === docId);
  const staffRoles = new Set(["admin", "designer", "engineer", "internal"]);
  const staffPrimary = new Set(["designer", "engineer", "internal", "admin"]);
  return isAuthKeyedStaffProfile && (staffRoles.has(role) || staffPrimary.has(primary));
}

export function canViewerGrantStaffCapabilities({
  viewerIsAdmin = false,
  viewerUid = "",
  targetProfile = null,
} = {}) {
  if (!viewerIsAdmin) return false;
  const targetUid = safeStr(targetProfile?.authUid || targetProfile?.id);
  if (!targetUid || !isStaffCapabilityGrantTarget(targetProfile)) return false;
  return safeStr(viewerUid) !== targetUid;
}
